// public/js/features/assets/trip-share-modal.js
// "Create a share link" — choose what the link reveals, then get the URL.
//
// The modal exists because the choice matters: a link needs no login, so
// whatever is ticked here is readable by anyone it is forwarded to. The
// fields on offer come from the SAME list the Lambda validates against
// (utils/share-fields.js), so nothing can be ticked that would be refused,
// and nothing can be sent that was not offered.
import { escapeHtml } from '../../utils/escape.js';
import { SHARE_FIELDS } from '../../utils/share-fields.js';

const SWITCHES = [
  ['route',    'Travelled route',   'The path on the map.'],
  ['position', 'Current position',  'Where the asset is now — keeps updating while the trip runs.'],
  ['stops',    'Stops along the way', 'When and how long it stood still.'],
  ['sensors',  'Sensor readings',   'Temperature, battery and shock, where recorded.'],
];

const EXPIRY_CHOICES = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, 'A year']];

const groupOf = (fields, group) => fields.filter(f => f.group === group);
const GROUPS = [...new Set(SHARE_FIELDS.map(f => f.group))];

/**
 * @param {{tripSummary?:string, onCreate:(scope:{}, days:number)=>Promise<{url:string}>}} opts
 */
export function openTripShareModal({ tripSummary = '', onCreate }) {
  const id = 'trip-share-modal';
  document.getElementById(id)?.remove();

  const html = `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-link me-2 text-secondary"></i>Share this trip</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="small text-muted mb-3">
              ${tripSummary ? `${escapeHtml(tripSummary)}<br>` : ''}
              Anyone with the link can see what you tick below, without signing in.
              Tick as little as the recipient needs.
            </p>

            <div class="mb-3">
              <div class="fw-semibold small mb-2">The journey</div>
              ${SWITCHES.map(([key, label, hint]) => `
                <div class="form-check mb-1">
                  <input class="form-check-input js-share-switch" type="checkbox" id="sw-${key}"
                         data-key="${key}" ${key === 'sensors' ? '' : 'checked'}>
                  <label class="form-check-label" for="sw-${key}">
                    ${label}<span class="d-block text-muted small">${hint}</span>
                  </label>
                </div>`).join('')}
            </div>

            <div class="mb-3">
              <div class="fw-semibold small mb-1">About the asset</div>
              <div class="text-muted small mb-2">Nothing is shared unless you tick it.</div>
              ${GROUPS.map(g => `
                <div class="mb-2">
                  <div class="text-muted" style="font-size:.72rem;text-transform:uppercase;letter-spacing:.06em">${escapeHtml(g)}</div>
                  ${groupOf(SHARE_FIELDS, g).map(f => `
                    <div class="form-check form-check-inline">
                      <input class="form-check-input js-share-field" type="checkbox" id="fld-${f.id}" data-field="${f.id}">
                      <label class="form-check-label small" for="fld-${f.id}">
                        ${escapeHtml(f.label)}${f.sensitive
                          ? ' <i class="fa-solid fa-triangle-exclamation text-warning" title="This names your company, not the shipment"></i>'
                          : ''}
                      </label>
                    </div>`).join('')}
                </div>`).join('')}
            </div>

            <div class="mb-1">
              <div class="fw-semibold small mb-2">Valid for</div>
              <div class="btn-group btn-group-sm" role="group">
                ${EXPIRY_CHOICES.map(([d, label], i) => `
                  <input type="radio" class="btn-check js-share-expiry" name="share-expiry"
                         id="exp-${d}" value="${d}" ${i === 1 ? 'checked' : ''}>
                  <label class="btn btn-outline-secondary" for="exp-${d}">${label}</label>`).join('')}
              </div>
              <div class="text-muted small mt-1">Counted from the end of the trip. You can revoke the link at any time.</div>
            </div>

            <div class="js-share-result mt-3"></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-primary js-share-go">Create link</button>
          </div>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
  const root = document.getElementById(id);
  const modal = new window.bootstrap.Modal(root);
  modal.show();
  root.addEventListener('hidden.bs.modal', () => root.remove());

  root.querySelector('.js-share-go').addEventListener('click', async (ev) => {
    const btn = ev.currentTarget;
    const scope = {};
    root.querySelectorAll('.js-share-switch').forEach(i => { scope[i.dataset.key] = i.checked; });
    scope.assetFields = [...root.querySelectorAll('.js-share-field')].filter(i => i.checked).map(i => i.dataset.field);
    const days = Number(root.querySelector('.js-share-expiry:checked')?.value || 30);

    btn.disabled = true;
    btn.textContent = 'Creating…';
    try {
      const made = await onCreate(scope, days);
      root.querySelector('.js-share-result').innerHTML = `
        <div class="alert alert-success py-2 px-3 mb-0 small">
          <div class="fw-semibold mb-1">Link created</div>
          <div class="input-group input-group-sm">
            <input type="text" class="form-control js-share-url" readonly value="${escapeHtml(made.url)}">
            <button class="btn btn-outline-secondary js-copy" type="button">Copy</button>
          </div>
          <div class="text-muted mt-1">Shown once — copy it now.</div>
        </div>`;
      root.querySelector('.js-copy').addEventListener('click', async () => {
        const input = root.querySelector('.js-share-url');
        try {
          await navigator.clipboard.writeText(input.value);
          window.showToast?.('Link copied', 'success');
        } catch { input.select(); }
      });
      // The settings produced this link; changing them now would be a lie.
      root.querySelectorAll('.js-share-switch,.js-share-field,.js-share-expiry')
        .forEach(i => { i.disabled = true; });
      btn.textContent = 'Create another';
      btn.disabled = false;
    } catch {
      window.showToast?.('Could not create the link', 'danger');
      btn.textContent = 'Create link';
      btn.disabled = false;
    }
  });
}
